import "../assets/css/package.css";
import "../components/WelcomeSection.css";

import { useEffect, useRef, useState } from "react";
import { RazorPayPayment } from "../Payment/RazorPayPayment";
import LoaderOverlay from "../Payment/LoaderOverlay";

interface InnerBannerProps {
    packageId: number;
    packageName: string;
    price: number;
    IsUpgrade: boolean;
    CouponCode: boolean;
    AddNewSponsorGrowniverseId: string;
}

const Payment: React.FC<InnerBannerProps> = ({
    packageId,
    packageName,
    price,
    IsUpgrade,
    CouponCode,
    AddNewSponsorGrowniverseId,
}) => {
    const [loader, setLoader] = useState(false);
    const [unPaidBalance, setUnPaidBalance] = useState(0);

    const runOnce = useRef(false);

    /* =========================================================
       LOAD USER / ACCOUNT
    ========================================================= */

    useEffect(() => {
        if (runOnce.current) return;

        try {
            const storedProfile = localStorage.getItem("userProfile");
            const storedAccount = localStorage.getItem("account");

            if (storedProfile) {
                JSON.parse(storedProfile);
            }

            if (storedAccount) {
                const userAccount = JSON.parse(storedAccount);

                setUnPaidBalance(
                    Number(userAccount?.unPaidBalance) || 0
                );
            }
        } catch (error) {
            console.error(
                "Unable to load payment information:",
                error
            );
        }

        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });

        runOnce.current = true;
    }, []);

    /* =========================================================
       PAYMENT
    ========================================================= */

    const handleCartPayment = () => {
        if (!price || price <= 0) {
            return;
        }

        setLoader(true);

        try {
            RazorPayPayment(
                price * 100,
                packageId,
                AddNewSponsorGrowniverseId,
                null
            );
        } catch (error) {
            console.error(
                "Payment initialization failed:",
                error
            );

            setLoader(false);
        }
    };

    /* =========================================================
       HELPERS
    ========================================================= */

    const formatPrice = (value: number) => {
        return new Intl.NumberFormat("en-IN").format(
            Number(value) || 0
        );
    };

    /* =========================================================
       RENDER
    ========================================================= */

    return (
        <>
            {loader && <LoaderOverlay open={loader} />}

            <section className="professional-payment-page">

                {/* =================================================
                    BACKGROUND
                ================================================= */}

                <div className="payment-background">
                    <div className="payment-grid" />

                    <div className="payment-glow payment-glow-one" />

                    <div className="payment-glow payment-glow-two" />

                    <div className="payment-glow payment-glow-three" />
                </div>

                {/* =================================================
                    MAIN CONTENT
                ================================================= */}

                <div className="payment-content">

                    {/* =================================================
                        HEADER
                    ================================================= */}

                    <div className="payment-page-heading">

                        <div className="payment-status-pill">
                            <span className="payment-status-dot" />
                            SECURE CHECKOUT
                        </div>

                        <h1>
                            Complete Your
                            <span> Purchase</span>
                        </h1>

                        <p>
                            You're one step away from accessing your
                            learning package.
                        </p>

                    </div>

                    {/* =================================================
                        CHECKOUT CARD
                    ================================================= */}

                    <div className="payment-card">

                        {/* =================================================
                            CARD HEADER
                        ================================================= */}

                        <div className="payment-card-header">

                            <div>
                                <span className="payment-mini-label">
                                    ORDER SUMMARY
                                </span>

                                <h2>
                                    Payment Details
                                </h2>
                            </div>

                            <div className="secure-badge">
                                <span>✓</span>
                                SECURE
                            </div>

                        </div>

                        {/* =================================================
                            PACKAGE
                        ================================================= */}

                        <div className="package-summary">

                            <div className="package-summary-icon">
                                ◈
                            </div>

                            <div className="package-summary-info">

                                <span>
                                    PREMIUM LEARNING
                                </span>

                                <strong>
                                    {packageName}
                                </strong>

                                <small>
                                    Lifetime learning access
                                </small>

                            </div>

                            <div className="package-summary-price">
                                ₹{formatPrice(price)}
                            </div>

                        </div>

                        {/* =================================================
                            DIVIDER
                        ================================================= */}

                        <div className="payment-divider" />

                        {/* =================================================
                            PRICE DETAILS
                        ================================================= */}

                        <div className="price-details">

                            <div className="price-line">
                                <span>
                                    Package Price
                                </span>

                                <strong>
                                    ₹{formatPrice(price)}
                                </strong>
                            </div>

                            {CouponCode && (
                                <div className="price-line discount-line">
                                    <span>
                                        Sponsor Discount
                                    </span>

                                    <strong>
                                        Applied
                                    </strong>
                                </div>
                            )}

                            <div className="price-line">
                                <span>
                                    Payment Method
                                </span>

                                <strong className="razorpay-text">
                                    Razorpay
                                </strong>
                            </div>

                        </div>

                        {/* =================================================
                            TOTAL
                        ================================================= */}

                        <div className="total-section">

                            <div>
                                <span>
                                    TOTAL AMOUNT
                                </span>

                                <small>
                                    Inclusive of applicable taxes
                                </small>
                            </div>

                            <strong>
                                ₹{formatPrice(price)}
                            </strong>

                        </div>

                        {/* =================================================
                            PAYMENT BUTTON
                        ================================================= */}

                        <button
                            className="professional-payment-button"
                            onClick={handleCartPayment}
                            disabled={!price || price <= 0}
                        >

                            <div className="payment-button-left">

                                <span className="payment-lock">
                                    🔒
                                </span>

                                <span>
                                    Proceed to Secure Payment
                                </span>

                            </div>

                            <span className="payment-arrow">
                                →
                            </span>

                        </button>

                        {/* =================================================
                            SECURITY NOTE
                        ================================================= */}

                        <div className="payment-security">

                            <span className="security-check">
                                ✓
                            </span>

                            <div>
                                <strong>
                                    Secure & encrypted payment
                                </strong>

                                <small>
                                    Your payment information is securely
                                    processed by Razorpay.
                                </small>
                            </div>

                        </div>

                    </div>

                    {/* =================================================
                        BENEFITS
                    ================================================= */}

                    <div className="payment-benefits">

                        <div className="payment-benefit">

                            <div>
                                ✓
                            </div>

                            <span>
                                Secure Checkout
                            </span>

                        </div>

                        <div className="payment-benefit">

                            <div>
                                ✓
                            </div>

                            <span>
                                Instant Access
                            </span>

                        </div>

                        <div className="payment-benefit">

                            <div>
                                ✓
                            </div>

                            <span>
                                Premium Learning
                            </span>

                        </div>

                    </div>

                    {/* =================================================
                        FOOTER NOTE
                    ================================================= */}

                    <div className="payment-footer-note">
                        <span>GROWNIVERSE</span>
                        <b>•</b>
                        <span>SECURE LEARNING EXPERIENCE</span>
                    </div>

                </div>

                {/* =================================================
                    PAGE STYLES
                ================================================= */}

                <style>
                    {`

                    /* =====================================================
                       MAIN PAGE
                    ===================================================== */

                    .professional-payment-page {
                        position: relative;
                        width: 100%;
                        min-height: 100vh;
                        box-sizing: border-box;
                        overflow: hidden;

                        background:
                            radial-gradient(
                                circle at 15% 20%,
                                rgba(91, 75, 255, 0.13),
                                transparent 32%
                            ),
                            radial-gradient(
                                circle at 85% 30%,
                                rgba(0, 229, 255, 0.08),
                                transparent 30%
                            ),
                            linear-gradient(
                                135deg,
                                #030305 0%,
                                #080810 50%,
                                #030305 100%
                            );

                        padding:
                            60px
                            clamp(20px, 5vw, 80px)
                            80px;

                        color: #ffffff;

                        box-sizing: border-box;
                    }


                    /* =====================================================
                       BACKGROUND
                    ===================================================== */

                    .payment-background {
                        position: absolute;
                        inset: 0;
                        overflow: hidden;
                        pointer-events: none;
                        z-index: 0;
                    }


                    .payment-grid {
                        position: absolute;
                        inset: 0;

                        background-image:
                            linear-gradient(
                                rgba(255,255,255,0.025) 1px,
                                transparent 1px
                            ),
                            linear-gradient(
                                90deg,
                                rgba(255,255,255,0.025) 1px,
                                transparent 1px
                            );

                        background-size:
                            55px 55px;

                        mask-image:
                            linear-gradient(
                                to bottom,
                                black,
                                transparent 85%
                            );

                        opacity: 0.5;
                    }


                    .payment-glow {
                        position: absolute;
                        border-radius: 50%;
                        filter: blur(100px);
                        opacity: 0.35;
                    }


                    .payment-glow-one {
                        width: 300px;
                        height: 300px;

                        left: -120px;
                        top: 100px;

                        background:
                            rgba(91, 75, 255, 0.22);
                    }


                    .payment-glow-two {
                        width: 280px;
                        height: 280px;

                        right: -100px;
                        top: 250px;

                        background:
                            rgba(0, 229, 255, 0.14);
                    }


                    .payment-glow-three {
                        width: 350px;
                        height: 350px;

                        left: 45%;
                        bottom: -220px;

                        background:
                            rgba(139, 92, 246, 0.10);
                    }


                    /* =====================================================
                       CONTENT
                    ===================================================== */

                    .payment-content {
                        position: relative;
                        z-index: 2;

                        width: 100%;
                        max-width: 900px;

                        margin: 0 auto;

                        box-sizing: border-box;
                    }


                    /* =====================================================
                       PAGE HEADING
                    ===================================================== */

                    .payment-page-heading {
                        text-align: center;

                        margin-bottom: 38px;
                    }


                    .payment-status-pill {
                        display: inline-flex;
                        align-items: center;
                        gap: 9px;

                        padding:
                            8px
                            14px;

                        border:
                            1px solid
                            rgba(255,255,255,0.09);

                        border-radius: 30px;

                        background:
                            rgba(255,255,255,0.035);

                        color:
                            rgba(255,255,255,0.55);

                        font-size: 9px;

                        letter-spacing: 2px;

                        font-weight: 700;

                        backdrop-filter:
                            blur(15px);
                    }


                    .payment-status-dot {
                        width: 6px;
                        height: 6px;

                        border-radius: 50%;

                        background:
                            #55e6a5;

                        box-shadow:
                            0 0 14px
                            rgba(85,230,165,0.8);
                    }


                    .payment-page-heading h1 {
                        margin:
                            20px
                            0
                            10px;

                        font-size:
                            clamp(32px, 5vw, 52px);

                        line-height: 1.05;

                        font-weight: 700;

                        letter-spacing: -1.5px;

                        color: #ffffff;
                    }


                    .payment-page-heading h1 span {
                        background:
                            linear-gradient(
                                90deg,
                                #ffffff,
                                #8b7cff,
                                #00e5ff
                            );

                        -webkit-background-clip: text;
                        background-clip: text;

                        -webkit-text-fill-color: transparent;
                    }


                    .payment-page-heading p {
                        margin: 0;

                        color:
                            rgba(255,255,255,0.42);

                        font-size: 14px;

                        line-height: 1.7;
                    }


                    /* =====================================================
                       PAYMENT CARD
                    ===================================================== */

                    .payment-card {
                        width: 100%;

                        box-sizing: border-box;

                        padding:
                            clamp(22px, 4vw, 38px);

                        border:
                            1px solid
                            rgba(255,255,255,0.09);

                        border-radius: 26px;

                        background:
                            linear-gradient(
                                145deg,
                                rgba(255,255,255,0.065),
                                rgba(255,255,255,0.025)
                            );

                        backdrop-filter:
                            blur(30px);

                        -webkit-backdrop-filter:
                            blur(30px);

                        box-shadow:
                            0 30px 80px
                            rgba(0,0,0,0.45),

                            inset 0 1px 0
                            rgba(255,255,255,0.05);

                        overflow: hidden;
                    }


                    /* =====================================================
                       CARD HEADER
                    ===================================================== */

                    .payment-card-header {
                        display: flex;

                        align-items: center;

                        justify-content: space-between;

                        gap: 20px;

                        margin-bottom: 28px;
                    }


                    .payment-mini-label {
                        display: block;

                        color:
                            #887bff;

                        font-size: 9px;

                        font-weight: 700;

                        letter-spacing: 2px;

                        margin-bottom: 7px;
                    }


                    .payment-card-header h2 {
                        margin: 0;

                        color: #ffffff;

                        font-size: 23px;

                        font-weight: 650;
                    }


                    .secure-badge {
                        display: flex;

                        align-items: center;

                        gap: 7px;

                        padding:
                            8px
                            11px;

                        border-radius: 20px;

                        background:
                            rgba(85,230,165,0.08);

                        border:
                            1px solid
                            rgba(85,230,165,0.18);

                        color:
                            #55e6a5;

                        font-size: 8px;

                        font-weight: 700;

                        letter-spacing: 1px;
                    }


                    .secure-badge span {
                        width: 17px;
                        height: 17px;

                        display: flex;

                        align-items: center;
                        justify-content: center;

                        border-radius: 50%;

                        background:
                            rgba(85,230,165,0.15);

                        font-size: 10px;
                    }


                    /* =====================================================
                       PACKAGE SUMMARY
                    ===================================================== */

                    .package-summary {
                        display: flex;

                        align-items: center;

                        gap: 16px;

                        padding: 17px;

                        border:
                            1px solid
                            rgba(255,255,255,0.07);

                        border-radius: 17px;

                        background:
                            rgba(0,0,0,0.22);
                    }


                    .package-summary-icon {
                        width: 48px;
                        height: 48px;

                        flex-shrink: 0;

                        display: flex;

                        align-items: center;
                        justify-content: center;

                        border-radius: 14px;

                        background:
                            linear-gradient(
                                135deg,
                                rgba(139,124,255,0.2),
                                rgba(0,229,255,0.08)
                            );

                        border:
                            1px solid
                            rgba(139,124,255,0.2);

                        color:
                            #a99fff;

                        font-size: 21px;

                        box-shadow:
                            0 0 25px
                            rgba(139,124,255,0.08);
                    }


                    .package-summary-info {
                        min-width: 0;

                        flex: 1;
                    }


                    .package-summary-info span {
                        display: block;

                        color:
                            rgba(255,255,255,0.36);

                        font-size: 8px;

                        letter-spacing: 1.5px;

                        margin-bottom: 4px;
                    }


                    .package-summary-info strong {
                        display: block;

                        color: #ffffff;

                        font-size: 14px;

                        font-weight: 600;

                        overflow: hidden;

                        text-overflow: ellipsis;

                        white-space: nowrap;
                    }


                    .package-summary-info small {
                        display: block;

                        margin-top: 4px;

                        color:
                            rgba(255,255,255,0.34);

                        font-size: 10px;
                    }


                    .package-summary-price {
                        flex-shrink: 0;

                        color:
                            #ffffff;

                        font-size: 17px;

                        font-weight: 700;
                    }


                    /* =====================================================
                       DIVIDER
                    ===================================================== */

                    .payment-divider {
                        height: 1px;

                        margin:
                            27px
                            0;

                        background:
                            rgba(255,255,255,0.07);
                    }


                    /* =====================================================
                       PRICE DETAILS
                    ===================================================== */

                    .price-details {
                        display: flex;

                        flex-direction: column;

                        gap: 15px;
                    }


                    .price-line {
                        display: flex;

                        align-items: center;

                        justify-content: space-between;

                        gap: 20px;

                        color:
                            rgba(255,255,255,0.48);

                        font-size: 13px;
                    }


                    .price-line strong {
                        color:
                            rgba(255,255,255,0.82);

                        font-size: 13px;
                    }


                    .discount-line {
                        color:
                            #55e6a5;
                    }


                    .discount-line strong {
                        color:
                            #55e6a5;
                    }


                    .razorpay-text {
                        color:
                            #8b7cff !important;
                    }


                    /* =====================================================
                       TOTAL
                    ===================================================== */

                    .total-section {
                        display: flex;

                        align-items: center;

                        justify-content: space-between;

                        gap: 20px;

                        margin-top: 27px;

                        padding:
                            20px 0;

                        border-top:
                            1px solid
                            rgba(255,255,255,0.07);

                        border-bottom:
                            1px solid
                            rgba(255,255,255,0.07);
                    }


                    .total-section span {
                        display: block;

                        color:
                            rgba(255,255,255,0.38);

                        font-size: 9px;

                        font-weight: 700;

                        letter-spacing: 1.5px;
                    }


                    .total-section small {
                        display: block;

                        margin-top: 5px;

                        color:
                            rgba(255,255,255,0.28);

                        font-size: 10px;
                    }


                    .total-section > strong {
                        color:
                            #55e6a5;

                        font-size:
                            clamp(24px, 4vw, 30px);

                        font-weight: 700;

                        white-space: nowrap;

                        text-shadow:
                            0 0 20px
                            rgba(85,230,165,0.12);
                    }


                    /* =====================================================
                       PAYMENT BUTTON
                    ===================================================== */

                    .professional-payment-button {
                        width: 100%;

                        display: flex;

                        align-items: center;

                        justify-content: space-between;

                        gap: 15px;

                        margin-top: 25px;

                        padding:
                            17px
                            20px;

                        border: 0;

                        border-radius: 15px;

                        cursor: pointer;

                        color: #ffffff;

                        background:
                            linear-gradient(
                                100deg,
                                #6657f5,
                                #7d6cff
                            );

                        box-shadow:
                            0 15px 35px
                            rgba(101,87,245,0.25);

                        font-size: 14px;

                        font-weight: 700;

                        transition:
                            transform .25s ease,
                            box-shadow .25s ease,
                            filter .25s ease;
                    }


                    .professional-payment-button:hover {
                        transform:
                            translateY(-3px);

                        box-shadow:
                            0 20px 45px
                            rgba(101,87,245,0.38);

                        filter:
                            brightness(1.08);
                    }


                    .professional-payment-button:active {
                        transform:
                            translateY(0);
                    }


                    .professional-payment-button:disabled {
                        opacity: 0.5;

                        cursor: not-allowed;

                        transform: none;
                    }


                    .payment-button-left {
                        display: flex;

                        align-items: center;

                        gap: 10px;
                    }


                    .payment-lock {
                        font-size: 15px;
                    }


                    .payment-arrow {
                        width: 32px;
                        height: 32px;

                        display: flex;

                        align-items: center;
                        justify-content: center;

                        border-radius: 10px;

                        background:
                            rgba(255,255,255,0.12);

                        font-size: 18px;
                    }


                    /* =====================================================
                       SECURITY
                    ===================================================== */

                    .payment-security {
                        display: flex;

                        align-items: flex-start;

                        gap: 11px;

                        margin-top: 20px;

                        padding:
                            14px;

                        border-radius: 13px;

                        background:
                            rgba(85,230,165,0.035);

                        border:
                            1px solid
                            rgba(85,230,165,0.08);
                    }


                    .security-check {
                        width: 22px;
                        height: 22px;

                        flex-shrink: 0;

                        display: flex;

                        align-items: center;
                        justify-content: center;

                        border-radius: 50%;

                        background:
                            rgba(85,230,165,0.10);

                        color:
                            #55e6a5;

                        font-size: 11px;

                        font-weight: 700;
                    }


                    .payment-security strong {
                        display: block;

                        color:
                            rgba(255,255,255,0.72);

                        font-size: 11px;

                        margin-bottom: 3px;
                    }


                    .payment-security small {
                        display: block;

                        color:
                            rgba(255,255,255,0.32);

                        font-size: 9px;

                        line-height: 1.5;
                    }


                    /* =====================================================
                       BENEFITS
                    ===================================================== */

                    .payment-benefits {
                        display: grid;

                        grid-template-columns:
                            repeat(3, 1fr);

                        gap: 12px;

                        margin-top: 18px;
                    }


                    .payment-benefit {
                        display: flex;

                        align-items: center;

                        justify-content: center;

                        gap: 8px;

                        min-height: 42px;

                        padding:
                            8px;

                        border:
                            1px solid
                            rgba(255,255,255,0.06);

                        border-radius: 12px;

                        background:
                            rgba(255,255,255,0.025);

                        color:
                            rgba(255,255,255,0.4);

                        font-size: 9px;

                        letter-spacing: .4px;
                    }


                    .payment-benefit div {
                        width: 18px;
                        height: 18px;

                        display: flex;

                        align-items: center;
                        justify-content: center;

                        border-radius: 50%;

                        background:
                            rgba(85,230,165,0.08);

                        color:
                            #55e6a5;

                        font-size: 9px;
                    }


                    /* =====================================================
                       FOOTER
                    ===================================================== */

                    .payment-footer-note {
                        display: flex;

                        align-items: center;

                        justify-content: center;

                        gap: 8px;

                        margin-top: 30px;

                        color:
                            rgba(255,255,255,0.18);

                        font-size: 8px;

                        letter-spacing: 1.5px;
                    }


                    .payment-footer-note b {
                        color:
                            rgba(139,124,255,0.5);
                    }


                    /* =====================================================
                       TABLET
                    ===================================================== */

                    @media (max-width: 900px) {

                        .professional-payment-page {
                            padding:
                                45px
                                28px
                                65px;
                        }

                        .payment-content {
                            max-width: 720px;
                        }

                    }


                    /* =====================================================
                       MOBILE
                    ===================================================== */

                    @media (max-width: 600px) {

                        .professional-payment-page {
                            padding:
                                30px
                                16px
                                50px;
                        }


                        .payment-page-heading {
                            margin-bottom: 28px;
                        }


                        .payment-page-heading h1 {
                            font-size: 34px;

                            letter-spacing: -1px;
                        }


                        .payment-page-heading p {
                            font-size: 12px;

                            max-width: 330px;

                            margin-left: auto;
                            margin-right: auto;
                        }


                        .payment-card {
                            padding: 20px;

                            border-radius: 21px;
                        }


                        .payment-card-header {
                            margin-bottom: 20px;
                        }


                        .payment-card-header h2 {
                            font-size: 19px;
                        }


                        .secure-badge {
                            padding:
                                7px
                                9px;

                            font-size: 7px;
                        }


                        .package-summary {
                            padding: 13px;

                            gap: 11px;
                        }


                        .package-summary-icon {
                            width: 40px;
                            height: 40px;

                            border-radius: 11px;

                            font-size: 17px;
                        }


                        .package-summary-info strong {
                            font-size: 12px;
                        }


                        .package-summary-info small {
                            font-size: 9px;
                        }


                        .package-summary-price {
                            font-size: 14px;
                        }


                        .payment-divider {
                            margin:
                                22px
                                0;
                        }


                        .price-line {
                            font-size: 12px;
                        }


                        .price-line strong {
                            font-size: 12px;
                        }


                        .total-section {
                            padding:
                                17px
                                0;
                        }


                        .total-section > strong {
                            font-size: 24px;
                        }


                        .professional-payment-button {
                            padding:
                                15px
                                16px;

                            font-size: 12px;
                        }


                        .payment-arrow {
                            width: 28px;
                            height: 28px;
                        }


                        .payment-benefits {
                            grid-template-columns: 1fr;

                            gap: 8px;
                        }


                        .payment-benefit {
                            justify-content: flex-start;

                            padding:
                                10px
                                13px;
                        }


                        .payment-footer-note {
                            margin-top: 25px;

                            font-size: 7px;
                        }

                    }


                    /* =====================================================
                       VERY SMALL MOBILE
                    ===================================================== */

                    @media (max-width: 380px) {

                        .professional-payment-page {
                            padding-left: 12px;
                            padding-right: 12px;
                        }


                        .payment-card {
                            padding: 17px;
                        }


                        .package-summary-price {
                            font-size: 13px;
                        }


                        .payment-page-heading h1 {
                            font-size: 30px;
                        }

                    }

                    `}
                </style>
            </section>
        </>
    );
};

export default Payment;